import Phaser from 'phaser';
import { generateAllTextures } from '../systems/util/textures.js';
import { COLORS, TEXTURE_KEYS, PROP_KEYS, BG_FAR_KEYS, BG_NEAR_KEYS } from '../systems/util/constants.js';
import { SaveManager } from '../systems/SaveManager.js';
import levels from '../../data/levels.json';

// Comic-art overhaul: real PNGs replace most procedural textures now (see
// public/assets/). `generateAllTextures` still runs afterward for whatever
// doesn't have supplied art (projectiles, spikes, exit door, particles) —
// the gen() calls for keys loaded here have been commented out in
// textures.js so nothing double-defines the same key.
const A = `${import.meta.env.BASE_URL}assets`;

export class Preload extends Phaser.Scene {
  constructor() {
    super('Preload');
  }

  preload() {
    this.cameras.main.setBackgroundColor(COLORS.white);
    const { width, height } = this.scale;
    this.add
      .text(width / 2, height / 2, 'LOADING…', {
        fontFamily: 'monospace',
        fontSize: '18px',
        color: '#140a24'
      })
      .setOrigin(0.5);

    // Main character (state-swapped poses, see Player.js)
    this.load.image(TEXTURE_KEYS.PLAYER, `${A}/main/main_idle.png`);
    this.load.image(TEXTURE_KEYS.PLAYER_RUN, `${A}/main/main_run.png`);
    this.load.image(TEXTURE_KEYS.PLAYER_JUMP, `${A}/main/main_jump.png`);
    this.load.image(TEXTURE_KEYS.PLAYER_SHOOT, `${A}/main/main_shoot.png`);
    this.load.image(TEXTURE_KEYS.PLAYER_PORTRAIT, `${A}/pickups/char.png`);

    // Enemies — only the surviving 3 soldiers / 2 drones / turret / boss
    // have art; the other ENEMY_CONFIGS entries are commented out in
    // Enemy.js so nothing references a texture that was never loaded.
    this.load.image(TEXTURE_KEYS.SOLDIER, `${A}/enemies/Soldier_1.png`);
    this.load.image(TEXTURE_KEYS.SOLDIER_SHIELD, `${A}/enemies/Soldier_2.png`);
    this.load.image(TEXTURE_KEYS.SOLDIER_HEAVY, `${A}/enemies/Soldier_3.png`);
    this.load.image(TEXTURE_KEYS.DRONE, `${A}/enemies/Drone_1.png`);
    this.load.image(TEXTURE_KEYS.DRONE_ELITE, `${A}/enemies/Drone_2.png`);
    this.load.image(TEXTURE_KEYS.TURRET, `${A}/enemies/Turret.png`);
    this.load.image(TEXTURE_KEYS.BOSS, `${A}/enemies/Boss_1.png`);

    // Floor / platform art
    this.load.image(TEXTURE_KEYS.FLOOR_TILE_1, `${A}/floor/Floor_tile_1.png`);
    this.load.image(TEXTURE_KEYS.FLOOR_TILE_2, `${A}/floor/Floor_tile_2.png`);
    this.load.image(TEXTURE_KEYS.PLATFORM_ART, `${A}/platform/Platform_1.png`);

    // Hazards
    this.load.image(TEXTURE_KEYS.HAZARD_FENCE, `${A}/hazards/Fence.png`);
    this.load.image(TEXTURE_KEYS.HAZARD_FIRE, `${A}/hazards/Fire.png`);

    // Pickups / HUD icons
    this.load.image(TEXTURE_KEYS.PICKUP_HEALTH, `${A}/pickups/hp.png`);
    this.load.image(TEXTURE_KEYS.PICKUP_AMMO, `${A}/pickups/ammo.png`);
    this.load.image(TEXTURE_KEYS.PICKUP_CHIP, `${A}/pickups/money.png`);
    this.load.image(TEXTURE_KEYS.HUD_GUN_ICON, `${A}/pickups/gun.png`);

    // Decorative props (non-colliding scatter dressing)
    for (let i = 0; i < PROP_KEYS.length; i++) {
      this.load.image(PROP_KEYS[i], `${A}/props/Props_${i + 1}.png`);
    }

    // Parallax background layers
    for (let i = 0; i < BG_FAR_KEYS.length; i++) {
      this.load.image(BG_FAR_KEYS[i], `${A}/background/layer_0_${i + 1}.png`);
    }
    for (let i = 0; i < BG_NEAR_KEYS.length; i++) {
      this.load.image(BG_NEAR_KEYS[i], `${A}/background/layer_3_${i + 1}.png`);
    }
  }

  create() {
    generateAllTextures(this);
    this._reduceContrast(PROP_KEYS, 0.05);
    SaveManager.syncWithLevelDefs(levels);

    this.time.delayedCall(120, () => this.scene.start('Menu'));
  }

  // Lightens each texture's opaque pixels toward white by `factor` (0-1),
  // leaving alpha untouched — reduces contrast against the white page
  // without making anything semi-transparent (Phaser's setTint can't do
  // this: it's a multiply blend, so black × any tint is still black; and
  // setAlpha would blend with whatever's behind it, causing overlapping
  // props to show a ghostly double-exposure where they cross). Runs once
  // here at load time on a scratch canvas, then re-registers the result as
  // the same texture key — the source PNGs on disk are never touched.
  _reduceContrast(keys, factor) {
    for (const key of keys) {
      const source = this.textures.get(key).getSourceImage();
      // A fresh canvas per texture — Phaser's addCanvas() keeps a
      // reference to the canvas itself (it doesn't copy pixels out of it),
      // so reusing one scratch canvas across iterations would leave every
      // texture pointing at whichever image was drawn last.
      const canvas = document.createElement('canvas');
      canvas.width = source.width;
      canvas.height = source.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(source, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const d = imageData.data;
      for (let i = 0; i < d.length; i += 4) {
        if (d[i + 3] === 0) continue;
        d[i] += (255 - d[i]) * factor;
        d[i + 1] += (255 - d[i + 1]) * factor;
        d[i + 2] += (255 - d[i + 2]) * factor;
      }
      ctx.putImageData(imageData, 0, 0);
      this.textures.remove(key);
      this.textures.addCanvas(key, canvas);
    }
  }
}
